import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Image, Trash2 } from 'lucide-react';
import FormFieldCalendar from '@/components/FormFieldCalendar';
import { FormFieldTimePicker } from '@/components/FormFieldTimePicker';
import FormFieldComponent from '@/components/FormFieldComponent';
import { FormFieldRadioGroup } from '@/components/FormFieldRadioGroup';
import { OneLineText } from '@/components/OneLineText';
import { ImageDisplayDialog } from '@/pages/caseManagement/processDetails/ImageDisplayDialog';
import { genUniqueId } from '@/lib/utils';
import {
  ATTEMPT_TYPE,
  MAX_IMAGE_LENGTH_ATTEMPT,
  type ImageType,
  type ServiceFields,
} from '@/types/workOrderAttempt.types';
import { NAME_VALIDATION } from '@/utils/constant';
import type { PsFirstAttemptHiFormValues } from '../psFirstAttemptHi.schema';

const DISABLED_STYLE = 'disabled:opacity-100 text-black';

type Props = {
  readOnly?: boolean;
};

export const PsFirstAttemptServiceInfoStep = ({ readOnly = false }: Props) => {
  const { control, getValues, setValue, watch, trigger } = useFormContext<PsFirstAttemptHiFormValues>();
  const type = watch('serviceAttempt.type');
  const images = (watch('serviceAttempt.images') as ImageType[] | null | undefined) ?? [];
  const [displayPreview, setDisplayPreview] = useState(false);
  const [previewImage, setPreviewImage] = useState<ImageType | null>(null);

  const resetServiceFields = () => {
    (
      Object.keys({
        personAccepting: null,
        relation: null,
        age: null,
        height: null,
        weight: null,
        hair: null,
        sex: null,
        race: null,
        otherDetails: null,
      }) as (keyof ServiceFields)[]
    ).forEach((key) => {
      setValue(`serviceAttempt.${key}`, null);
    });
  };

  return (
    <div className="flex flex-col gap-4 w-full mt-11">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormFieldCalendar
          name={`serviceAttempt.dateOfAttempt`}
          control={control}
          label="Date of Attempt"
          placeholder="Select Date"
          required
          disabled={readOnly}
          disabledStyle={DISABLED_STYLE}
          disabledAt={(date) => date > new Date()}
        />
        <FormFieldTimePicker
          name={`serviceAttempt.timeOfAttempt`}
          label="Time of Attempt"
          required
          use12HourFormat
          disabled={readOnly}
          disabledStyle={DISABLED_STYLE}
          placeHolder="Select Time"
          timeFields={['H', 'M']}
        />
      </div>
      <FormFieldComponent
        name={`serviceAttempt.serverName`}
        control={control}
        label="Server"
        disabled
        disabledStyle={DISABLED_STYLE}
      />
      <FormFieldComponent
        name={`serviceAttempt.additionalInformation`}
        control={control}
        label="Additional Information"
        disabled={readOnly}
        disabledStyle={DISABLED_STYLE}
        placeholder="Enter Additional Information"
      />
      <FormFieldComponent
        name={`serviceAttempt.description`}
        control={control}
        label="Description of Service"
        disabled={readOnly}
        disabledStyle={DISABLED_STYLE}
        placeholder="Enter Description of Service"
      />

      <div className="flex flex-col gap-2">
        {!readOnly && (
          <FormFieldComponent
            name={`serviceAttempt.images`}
            control={control}
            type="multipleFile"
            defaultFileName="Click to upload image"
            acceptedFileTypes={['jpeg', 'png', 'heif', 'heic', 'raw', 'jpg']}
            disabled={images.length >= MAX_IMAGE_LENGTH_ATTEMPT}
            onChange={(e) => {
              if (!e.target.files || e.target.files.length === 0) return;
              const newFiles: ImageType[] = Array.from(e.target.files).map((file) => ({
                id: genUniqueId(),
                name: file.name,
                image: file,
              }));
              const currentImages = (getValues(`serviceAttempt.images`) as ImageType[] | null | undefined) ?? [];
              setValue(`serviceAttempt.images`, [...currentImages, ...newFiles], {
                shouldDirty: true,
                shouldValidate: true,
                shouldTouch: true,
              });
            }}
          />
        )}
        {!readOnly && (
          <div className="flex justify-end w-full mr-10 text-[13px]">{`${images.length || 0} of 50`}</div>
        )}
        {displayPreview && (
          <ImageDisplayDialog
            attachments={(getValues(`serviceAttempt.images`) as ImageType[]) ?? []}
            onClose={() => setDisplayPreview(false)}
            attachmentId={previewImage?.id ?? ''}
          />
        )}
        <div className="grid grid-cols-2 gap-6 md:w-[80%] py-2">
          {images.map((img) => (
            <div
              key={img.id}
              className="flex justify-between h-[56px] bg-[#F5F5F5] rounded-md items-center p-4 text-primary"
            >
              <div
                className="flex gap-2 items-center cursor-pointer"
                onClick={() => {
                  setPreviewImage(img);
                  setDisplayPreview(true);
                }}
              >
                <Image className="h-5 w-5 text-primary" />
                <OneLineText text={img.name} className="flex mr-5" />
              </div>
              {!readOnly && (
                <Trash2
                  onClick={() => {
                    setValue(
                      `serviceAttempt.images`,
                      images.filter((i) => i.id !== img.id),
                      { shouldDirty: true, shouldValidate: true, shouldTouch: true }
                    );
                  }}
                  className="cursor-pointer text-error h-5 w-5"
                />
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="w-full gap-0 flex flex-col rounded-md border-bordercolor border-[1px]">
        <div className="rounded-t-md font-semibold bg-[#F5F5F5] px-4 py-4">
          <span>Service Information</span>
        </div>
        <div className="rounded-b-md flex flex-col px-4 py-4 gap-6">
          <FormFieldRadioGroup
            label="Type"
            direction="horizontal"
            name={`serviceAttempt.type`}
            options={[
              { value: ATTEMPT_TYPE.PERSONAL, label: 'Personal' },
              { value: ATTEMPT_TYPE.SUB, label: 'Sub' },
              { value: ATTEMPT_TYPE.BY_MAIL, label: 'Served by Mail' },
              { value: ATTEMPT_TYPE.BY_EMAIL, label: 'Served by E-mail' },
              { value: ATTEMPT_TYPE.ATTEMPTED, label: 'Attempted' },
              { value: ATTEMPT_TYPE.NOT_SERVED, label: 'Not Served' },
              { value: ATTEMPT_TYPE.POSTED, label: 'Posted' },
            ]}
            required
            disabled={readOnly}
            onChange={() => {
              resetServiceFields();
              void trigger('serviceAttempt');
            }}
          />
          {(type === ATTEMPT_TYPE.PERSONAL || type === ATTEMPT_TYPE.SUB) && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormFieldComponent
                  name={`serviceAttempt.personAccepting`}
                  control={control}
                  label="Person Accepting"
                  placeholder="Enter Person Accepting"
                  disabled={readOnly}
                  disabledStyle={DISABLED_STYLE}
                  {...NAME_VALIDATION}
                  required
                />
                <FormFieldComponent
                  name={`serviceAttempt.relation`}
                  control={control}
                  label="Title/ Relation to Serve"
                  placeholder="Enter Title/ Relation to Servee"
                  capitalizeFirstLetter
                  allowSpecialCharacters={false}
                  disabled={readOnly}
                  disabledStyle={DISABLED_STYLE}
                  allowNumber={false}
                  required
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <FormFieldComponent
                  name={`serviceAttempt.age`}
                  control={control}
                  label="Age"
                  disabled={readOnly}
                  disabledStyle={DISABLED_STYLE}
                  placeholder="Enter Age"
                  type="number"
                  maxDecimalPlaces={0}
                  allowDecimals={false}
                  required
                />
                <FormFieldComponent
                  name={`serviceAttempt.height`}
                  control={control}
                  disabled={readOnly}
                  disabledStyle={DISABLED_STYLE}
                  label="Height (feet and inch)"
                  placeholder="Enter Height"
                  required
                />
                <FormFieldComponent
                  name={`serviceAttempt.weight`}
                  control={control}
                  disabled={readOnly}
                  disabledStyle={DISABLED_STYLE}
                  label="Weight(lbs)"
                  placeholder="Enter Weight(lbs)"
                  type="number"
                  required
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <FormFieldComponent
                  name={`serviceAttempt.hair`}
                  control={control}
                  label="Hair Color"
                  disabled={readOnly}
                  disabledStyle={DISABLED_STYLE}
                  placeholder="Enter Hair Color"
                  replaceRegex={/[^a-zA-Z]/g}
                  capitalizeFirstLetter
                  required
                />
                <FormFieldComponent
                  name={`serviceAttempt.sex`}
                  control={control}
                  label="Sex"
                  disabled={readOnly}
                  disabledStyle={DISABLED_STYLE}
                  placeholder="Enter Sex"
                  replaceRegex={/[^a-zA-Z]/g}
                  capitalizeFirstLetter
                  required
                />
                <FormFieldComponent
                  name={`serviceAttempt.race`}
                  control={control}
                  label="Race"
                  disabled={readOnly}
                  disabledStyle={DISABLED_STYLE}
                  placeholder="Enter Race"
                  replaceRegex={/[^a-zA-Z]/g}
                  capitalizeFirstLetter
                  required
                />
              </div>
              <FormFieldComponent
                name={`serviceAttempt.otherDetails`}
                control={control}
                disabled={readOnly}
                disabledStyle={DISABLED_STYLE}
                label="Other"
                placeholder="Enter"
                capitalizeFirstLetter
              />
            </>
          )}
          {type === ATTEMPT_TYPE.BY_EMAIL && (
            <>
              <FormFieldComponent
                name={`serviceAttempt.emailAddress`}
                control={control}
                label="Email Address"
                placeholder="Enter Email Address"
                disabled={readOnly}
                disabledStyle={DISABLED_STYLE}
                required
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormFieldCalendar
                  name={`serviceAttempt.emailSentDate`}
                  control={control}
                  label="Email Sent Date"
                  placeholder="Select Date"
                  disabled={readOnly}
                  disabledStyle={DISABLED_STYLE}
                />
                <FormFieldCalendar
                  name={`serviceAttempt.emailAcknowledgementDate`}
                  control={control}
                  label="Email Acknowledgement Date"
                  placeholder="Select Date"
                  disabled={readOnly}
                  disabledStyle={DISABLED_STYLE}
                />
              </div>
              <FormFieldComponent
                name={`serviceAttempt.remarks`}
                control={control}
                label="Email Remarks"
                placeholder="Enter Remarks for 'Affidavit of Service by Electronic Mail'"
                disabled={readOnly}
                disabledStyle={DISABLED_STYLE}
              />
            </>
          )}
          {type === ATTEMPT_TYPE.BY_MAIL && (
            <FormFieldComponent
              name={`serviceAttempt.trackingNumber`}
              control={control}
              label="Certified Mail Number"
              placeholder="Enter Certified Mail Number"
              disabled={readOnly}
              disabledStyle={DISABLED_STYLE}
            />
          )}
        </div>
      </div>
    </div>
  );
};
